import { TElement } from '@udecode/plate';
import { API } from 'src/api';

import { Task, TypeEnum } from '../types/tasks';

export async function addTask(props: {
  boardId: string;
  colId: string;
  type: string;
  taskName: TElement[];
  assigner?: string;
  assignee?: string[];
  duplicate?: string[];
  startDate?: string;
  endDate?: string;
  description?: any[];
  priority?: string;
  label?: string;
  creation?: string;
  attachments?: any[];
}) {
  try {
    const payload = {
      board: props.boardId,
      column: props.colId,
      name: props.taskName,
      type: props.type,
      assigner: props.assigner,
      assignee: props.assignee,
      duplicate: props.duplicate,
      start_date: props?.startDate,
      end_date: props?.endDate,
      description: props.description,
      priority: props.priority,
      label: props.label,
      creation: props.type === 'creation' ? props.creation : undefined,
      attachments: props.attachments,
    };

    const response = await API.post('/board/task', payload);
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function deleteTask({ id }: { id: string }) {
  try {
    const response = await API.delete(`/board/task?id=${id}`);
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function duplicateTask({ id }: { id: string }) {
  try {
    const response = await API.post('board/task/duplicate', { task: id });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}
export async function updateTaskProperties({
  taskId,
  properties,
}: {
  taskId: string;
  properties: {
    assignee?: string[];
    assigner?: string;
    start_date?: string;
    end_date?: string;
    [key: string]: any;
  };
}) {
  try {
    const payload: any = { ...properties };

    payload.id = taskId;
    const response = await API.put('/board/task', payload);
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function fetchTask({ id, type }: { id: string; type?: string }) {
  try {
    const response = await API.get(
      type ? `/board/task?id=${id}&mode=shared` : `/board/task?id=${id}`
    );
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}
export async function fetchEpicTask({ id, type }: { id: string; type?: string }) {
  try {
    const response = await API.get(
      type ? `/board/task?id=${id}&mode=shared` : `/board/task?id=${id}&mode=agile`
    );
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function addComment({ content, itemId, text, tags, boardId, uuid }) {
  try {
    const response = await API.post(`board/comment`, {
      board: boardId,
      text: text,
      task: itemId,
      data: content,
      tags: tags,
      uuid: uuid,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function updateComment({ content, commentId, tags, text }) {
  try {
    const response = await API.put(`board/comment`, {
      comment: commentId,
      data: content,
      text: text,
      tags: tags,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function deleteComment({ commentId }) {
  try {
    const response = await API.delete(`board/comment?id=${commentId}`);
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function moveTask({
  boardId,
  taskId,
  sourceColId,
  destColId,
  newItemIndex,
}: {
  boardId: string;
  taskId: string;
  sourceColId: string;
  destColId: string;
  newItemIndex: number;
}) {
  try {
    const response = await API.put('board/column', {
      board: boardId,
      task_move: {
        id: taskId,
        src_col: sourceColId,
        dest_col: destColId,
        index: newItemIndex,
      },
    });
    if (response.data.success) {
      return true;
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function useCreationsForBoards() {
  try {
    const response = await API.get('board/creation');
    return response;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function useLabelsForBoards({ id }) {
  try {
    const response = await API.get(`board/label/${id}`);
    return response;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function moveTaskToAnotherBoard({ taskIds, destBoardId, destColId }) {
  try {
    const response = await API.post('board/task/move', {
      board: destBoardId,
      tasks: taskIds,
      column: destColId,
    });
    if (response.data.success) {
      return true;
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function toggleUpovteTask(taskId: string, upvote = true) {
  try {
    const response = await API.post('/board/vote', {
      task: taskId,
      vote: upvote ? 1 : -1,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function toggleWatchTask(taskId: string, enable = true) {
  try {
    const response = await API.post('/board/task/watch', {
      id: taskId,
      watch: enable,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function addFile(taskId: string, fileName: string, filePath: string) {
  try {
    const response = await API.post('/board/task/file', {
      task: taskId,
      add: [{ name: fileName, url: filePath }],
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function removeFile(taskId: string, filePath: string) {
  try {
    const response = await API.post('/board/task/file', {
      task: taskId,
      remove: [filePath],
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function shareTask(taskId: string, access: string, members: any, mode: string) {
  try {
    let payload = {};
    if (mode === 'add') {
      const memberPayload = {
        members: members.filter((obj) => !obj.isNew).map((obj) => obj.memberId),
        emails: members.filter((obj) => obj.isNew).map((obj) => obj.email),
      };
      payload = {
        task: taskId,
        access: access,
        add: memberPayload,
      };
    } else {
      payload = {
        task: taskId,
        ...(mode === 'edit' && { access: access, edit: members.member.memberId }),
        ...(mode === 'remove' && { remove: members.member.memberId }),
      };
    }
    const response = await API.put('/board/task/share', payload);
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function markTaskAsComplete(taskId: string, complete) {
  try {
    const response = await API.post('/board/task/complete', {
      id: taskId,
      completed: complete,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function createSubTask(parentTaskId: string, title) {
  try {
    const response = await API.post('/board/task/subtask', { parent: parentTaskId, name: title });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function deleteSubTask(taskId: string) {
  try {
    const response = await API.delete(`/board/task/subtask?id=${taskId}`);
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function updateSubTaskIndex(taskId: string, index: number) {
  try {
    const response = await API.put(`/board/task/subtask`, { id: taskId, index: index });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function fetchTimer() {
  try {
    const response = await API.get(`/task/user/timer`);
    if (response.data.success) {
      return response.data.data;
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function fetchTaskTimelog(taskId: string) {
  try {
    const response = await API.get(`/timer/timelog?task=${taskId}`);
    if (response.data.success) {
      return response.data.data.map((obj) => formatTaskTimelog(obj));
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function addCustomField(
  taskId: string,
  fieldId: string,
  type: TypeEnum,
  label: string,
  data: any,
  index: number,
  boardId?: string,
  taskTemplateRef?: string
) {
  try {
    const response = await API.post('board/task/field', {
      id: taskId,
      board: boardId,
      taskTemplateRef: taskTemplateRef,
      fieldId: fieldId,
      type: type,
      label: label,
      data: data,
      index: index,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function addCustomTemplateField(
  fieldId: string,
  type: TypeEnum,
  label: string,
  data: any,
  taskTemplateRef?: string
) {
  try {
    const response = await API.post('board/task/field', {
      taskTemplateRef: taskTemplateRef,
      fieldId: fieldId,
      type: type,
      label: label,
    });
    if (response.data.success) {
      return true;
    }
  } catch (error) {
    console.error(error);
    return false;
  }

  return false;
}

export async function deleteCustomField(taskId: string, fieldId: string, taskTemplateRef?: string) {
  try {
    const query = taskTemplateRef
      ? `board/task/field?task=${taskId}&id=${fieldId}&taskTemplateRef=${taskTemplateRef}`
      : `board/task/field?task=${taskId}&id=${fieldId}`;
    const response = await API.delete(query);
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}
export async function deleteCustomTemplateField(
  taskId: string,
  fieldId: string,
  taskTemplateRef?: string
) {
  try {
    const response = await API.delete(
      `board/task/field?task=${taskId}&id=${fieldId}&taskTemplateRef=${taskTemplateRef}`
    );
    if (response.data.success) {
      return true;
    }
  } catch (error) {
    console.error(error);
    return false;
  }
  return false;
}

export async function updateCustomFieldName(
  taskId: string,
  fieldId: string,
  label: string,
  taskTemplateRef?: string
) {
  try {
    const response = await API.put(`board/task/field`, {
      task: taskId,
      fieldId: fieldId,
      label: label,
      taskTemplateRef: taskTemplateRef,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}
export async function updateCustomTemplateFieldName(
  fieldId: string,
  label: string,
  taskTemplateRef?: string
) {
  try {
    const response = await API.put(`board/task/field`, {
      fieldId: fieldId,
      label: label,
      taskTemplateRef: taskTemplateRef,
    });
    if (response.data.success) {
      return true;
    }
  } catch (error) {
    console.error(error);
    return false;
  }
  return false;
}

export async function onUpdateCustomFieldType(
  taskId: string,
  fieldId: string,
  label: string,
  type: string,
  taskTemplateRef?: string
) {
  try {
    const response = await API.put(`board/task/field`, {
      task: taskId,
      fieldId: fieldId,
      type: type,
      label: label,
      taskTemplateRef: taskTemplateRef,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}
export async function updateCustomTemplateFieldType(
  fieldId: string,
  label: string,
  type: string,
  taskTemplateRef?: string
) {
  try {
    const response = await API.put(`board/task/field`, {
      fieldId: fieldId,
      type: type,
      label: label,
      taskTemplateRef: taskTemplateRef,
    });
    if (response.data.success) {
      return true;
    }
  } catch (error) {
    console.error(error);
    return false;
  }
  return false;
}

export async function updateCustomFieldData(
  taskId: string,
  fieldId: string,
  data: any,
  added?: any,
  taskTemplateRef?: string
) {
  try {
    const response = await API.put(`board/task/field`, {
      task: taskId,
      fieldId: fieldId,
      data: data,
      added: added,
      taskTemplateRef: taskTemplateRef,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function reorderCustomField(
  taskId: string,
  fieldId: string,
  index: number,
  taskTemplateRef?: string
) {
  try {
    const response = await API.put(`board/task/field`, {
      task: taskId,
      fieldId: fieldId,
      index,
      taskTemplateRef: taskTemplateRef,
    });
    if (response.data.success) {
      return response.data.data;
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}
export async function updateCustomTemplateFieldData(
  fieldId: string,
  data: any,
  added?: any,
  taskTemplateRef?: string
) {
  try {
    const response = await API.put(`board/task/field`, {
      fieldId: fieldId,
      data: data,
      added: added,
      taskTemplateRef: taskTemplateRef,
    });
    if (response.data.success) {
      return true;
    }
  } catch (error) {
    console.error(error);
    return false;
  }
  return false;
}

export async function duplicateTaskTemplate(id: string) {
  try {
    const response = await API.post('/board/task/template/duplicate', {
      taskTemplateRef: id,
    });
    if (response.data.success) {
      const res = formatTemplate(response.data.data);
      return res;
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

//Create and save template (same API)
export async function saveTemplate(templateName: string, customFieldIds: any, taskId: string) {
  try {
    const response = await API.post('/board/task/template', {
      name: templateName,
      customFieldIds: customFieldIds,
      taskId,
    });

    if (response.data.success) {
      // const template = formatTask(response.data.data);
      const template = formatTemplate(response.data.data);
      return template;
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function importTemplate(taskTemplateId: string, taskId: string) {
  try {
    const response = await API.post('/board/task/template/use', {
      taskTemplateRef: taskTemplateId,
      taskId: taskId,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}
export async function updateTemplate(templateId, templateName) {
  try {
    const response = await API.put(`/board/task/template`, {
      id: templateId,
      name: templateName,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function updateSubtaskParent(subtaskId: string, parentTaskId: string) {
  try {
    const response = await API.put(`/board/task/`, {
      id: subtaskId,
      parent: parentTaskId,
    });
    if (response.data.success) {
      return formatTask(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function sendForApproval({
  taskId,
  approversAdded,
  approversRemoved,
  minApprovers,
}: {
  taskId: string;
  approversAdded: string[];
  approversRemoved: string[];
  minApprovers: number;
}) {
  try {
    const payload = {
      ...(approversAdded.length > 0 && { addApprovers: approversAdded }),
      minimumApprovals: minApprovers,
      task: taskId,
      addApprovers: approversAdded,
      removeApprovers: approversRemoved,
      type: 'update_approvers',
    };
    const response = await API.put('/board/task/approval', payload);
    if (response.data.success) {
      return formatTaskApproval(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}
export async function sendTaskApprovalStatus({
  taskId,
  comments,
  status,
}: {
  taskId: string;
  comments?: string;
  status: string;
}) {
  try {
    const payload = {
      task: taskId,
      approval: status,
      comment: comments,
      type: 'approval_completed',
    };
    const response = await API.put('/board/task/approval', payload);
    if (response.data.success) {
      return formatTaskApproval(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function requestTaskChanges({
  taskId,
  comments,
}: {
  taskId: string;
  comments?: string;
}) {
  try {
    const payload = {
      task: taskId,
      comment: comments,
      type: 'request_change',
    };
    const response = await API.put('/board/task/approval', payload);
    if (response.data.success) {
      return formatTaskApproval(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function withdrawApproval({ taskId }: { taskId: string }) {
  try {
    const payload = {
      task: taskId,
      type: 'withdraw_request',
    };
    const response = await API.put('/board/task/approval', payload);
    if (response.data.success) {
      return formatTaskApproval(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function resendRequest({ taskId, member }: { taskId: string; member: string }) {
  try {
    const payload = {
      task: taskId,
      approver: member,
      type: 'reassign_approver',
    };
    const response = await API.put('/board/task/approval', payload);
    if (response.data.success) {
      return formatTaskApproval(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

export async function fetchApproval({ taskId }: { taskId: string }) {
  try {
    const response = await API.get(`/board/task/approval?task=${taskId}`);
    if (response.data.success) {
      return formatTaskApproval(response.data.data[0]);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}
export async function resolveRequest({
  taskId,
  comments,
  id,
}: {
  taskId: string;
  comments: string;
  id: string;
}) {
  try {
    const payload = {
      task: taskId,
      comment: comments,
      requestChange: id,
      type: 'resolve_request_change',
    };
    const response = await API.put('/board/task/approval', payload);
    if (response.data.success) {
      return formatTaskApproval(response.data.data);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

const useTasksService = () => {
  return {
    addTask,
    deleteTask,
    updateTaskProperties,
    fetchTask,
    addComment,
    updateComment,
    deleteComment,
    useCreationsForBoards,
    moveTask,
    useLabelsForBoards,
    moveTaskToAnotherBoard,
    toggleUpovteTask,
    shareTask,
    addFile,
    removeFile,
    markTaskAsComplete,
    createSubTask,
    deleteSubTask,
    updateSubTaskIndex,
    fetchTimer,
    fetchTaskTimelog,
    addCustomField,
    deleteCustomField,
    deleteCustomTemplateField,
    updateCustomFieldName,
    updateCustomTemplateFieldName,
    onUpdateCustomFieldType,
    updateCustomTemplateFieldType,
    updateCustomFieldData,
    reorderCustomField,
    updateCustomTemplateFieldData,
    addCustomTemplateField,
    duplicateTaskTemplate,
    saveTemplate,
    importTemplate,
    updateTemplate,
    updateSubtaskParent,
    sendForApproval,
    sendTaskApprovalStatus,
    requestTaskChanges,
    withdrawApproval,
    resendRequest,
    resolveRequest,
    fetchApproval,
  };
};

export default useTasksService;
export interface useBoardsReturnType<DataType> {
  data: DataType;
  error: unknown;
  isLoading: boolean;
  total: number;
  revalidate: () => Promise<boolean>;
}

export interface Column {
  items: Task[];
  name: string;
  color: string;
  id: string;
}

function formatTemplate(data) {
  data = { id: data._id, ...data };
  data.fields = data.fields
    ? data.fields.map((field) => {
        return { id: field._id, ...field };
      })
    : [];
  return data;
}

function formatTask(data): Task & { boardId: string; columnId: string } {
  return {
    ...data,
    boardId: data.board,
    columnId: data.column,
    id: data._id,
    fileId: data.file,

    assignDate:
      data.start_date && data.end_date
        ? [data.start_date, data.end_date]
        : data.end_date
        ? [data.end_date]
        : [],

    subtasks:
      data.subtasks
        ?.sort((a, b) => {
          return (a.subtaskIndex || 0) - (b.subtaskIndex || 0);
        })
        .map((subTask) => formatTask(subTask)) || [],

    timeLog: data.timelogs?.map((obj) => formatTaskTimelog(obj)),

    recurring: data?.recurring
      ? {
          recurringId: data?.recurring?._id,
          type: data?.recurring?.recurring?.recurring_type,
          repeats: data?.recurring?.recurring?.recur_on,
          ...(data?.recurring?.recurring?.recurring_type === 'custom' && {
            custom: Object.keys(data?.recurring?.recurring?.recur_periodic)[0],
            interval: Object.values(data?.recurring?.recurring?.recur_periodic)[0],
          }),
          disable: false,
        }
      : {},

    board: {
      id: data?.board?._id,
      ...data?.board,
    },

    approval: {
      approvers: data?.approvers?.map((obj) => ({ member: obj.member, status: obj.status })),
      changeRequests: data?.changeRequests?.map((obj) => ({
        member: obj?.member,
        comment: obj?.comment,
        _id: obj?._id,
      })),
      //Empty approval history shows edit approvers page
      approvalHistory:
        data?.history?.map((obj) => ({
          members: obj?.item.approvers,
          timestamp: obj.createdAt,
          ...obj.item,
        })) || [],
      isTaskWithdrawn: data?.approvalCycle === 'withdrawn' || false,
      minApprovalsRequired: data?.minimumApprovals,
      approvalRequestedBy: data?.approvalRequestedBy,
      approvalCycle: data?.approvalCycle,
    },

    approvalStatus:
      data?.approvers?.length > 0
        ? data?.approvers.filter((item) => item.status === 'approved').length >=
          data?.minimumApprovals
          ? 'approved'
          : data?.approvers.filter((item) => item.status === 'rejected').length >
            data?.approvers.length - data?.minimumApprovals
          ? 'rejected'
          : 'pending'
        : undefined,
    items: data?.items || [],
    reminderOffset: data?.reminder?.fire_at,
    startDate: data?.start_date,

    workspaceId: typeof data.workspace !== 'string' ? data.workspace?.workspaceId : data.workspace,
    endDate: data?.end_date,
    parentId: typeof data.parent === 'string' ? data.parent : data.parent?._id,
  };
}

function formatTaskApproval(data) {
  return {
    approvers: data?.approvers?.map((obj) => ({ member: obj.member, status: obj.status })),
    changeRequests: data?.changeRequests?.map((obj) => ({
      member: obj?.member,
      comment: obj?.comment,
      _id: obj?._id,
    })),
    //Empty approval history shows edit approvers page
    approvalHistory:
      data?.history?.map((obj) => ({
        members: obj?.item.approvers,
        timestamp: obj.createdAt,
        ...obj.item,
      })) || [],
    approvalRequestedBy: data?.approvalRequestedBy,
    isTaskWithdrawn: data?.approvalCycle === 'withdrawn' || false,
    minApprovalsRequired: data?.minApprovalsRequired,
    approvalCycle: data?.approvalCycle,
  };
}

function formatTaskTimelog(data) {
  //time tracked in minutes
  const timeTracked = data.logs
    .map(
      (log) => (parseInt(log?.duration?.hours) || 0) * 60 + parseInt(log?.duration?.minutes || 0)
    )
    .reduce((a, b) => a + b, 0);
  return {
    member: data.member,
    requestedBy: data?.requestedBy,
    logs: data.logs?.map((log) => {
      return { ...log };
    }),
    totalTimeTracked: timeTracked,
  };
}
